import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, LockKeyhole, ToggleLeft } from "lucide-react";
import { type FormEvent, useState } from "react";
import { request } from "../api";
import { useAuth } from "../auth";
import { ErrorState, LoadingState, PageHeader, Panel, StatusPill } from "../components";
import { formatTime } from "../format";
import type { EngineSetting } from "../types";

export function SettingsPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EngineSetting>();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => request<EngineSetting[]>("/settings"),
  });
  const update = useMutation({
    mutationFn: ({ setting, value, reason }: { setting: EngineSetting; value: string | boolean; reason: string }) => request<EngineSetting>(
      `/settings/${encodeURIComponent(setting.key)}`,
      { method: "PATCH", body: JSON.stringify({ value, expected_version: setting.version, reason }) },
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
      setEditing(undefined);
    },
  });

  return (
    <>
      <PageHeader
        eyebrow="Engine policy / safe allowlist"
        title="Settings the running engine can adopt"
        description="Only validated, hot-reloadable controls are editable. Infrastructure and secret values remain environment-owned."
      />
      {settings.isLoading && <LoadingState />}
      {settings.error && <ErrorState error={settings.error} />}
      <div className="settings-list">
        {settings.data?.map((setting) => (
          <Panel key={setting.key} className="setting-row">
            <div className="setting-row__icon">{setting.type === "cron" ? <Clock3 size={19} /> : <ToggleLeft size={19} />}</div>
            <div className="setting-row__content">
              <div><code>{setting.key}</code><StatusPill value={setting.source} /></div>
              <p>{setting.description}</p>
              <small>Default: <strong>{String(setting.default)}</strong>{setting.updated_at ? ` · Updated ${formatTime(setting.updated_at)}` : ""}</small>
            </div>
            <div className="setting-row__value">
              <small>Effective value</small>
              <strong>{typeof setting.value === "boolean" ? (setting.value ? "Enabled" : "Disabled") : String(setting.value)}</strong>
            </div>
            {session?.user.role === "admin" ? (
              <button type="button" className="button button--secondary" onClick={() => setEditing(setting)}>Change</button>
            ) : (
              <span className="setting-row__locked"><LockKeyhole size={14} /> Admin only</span>
            )}
          </Panel>
        ))}
      </div>
      {editing && (
        <SettingEditor
          setting={editing}
          busy={update.isPending}
          error={update.error?.message}
          onClose={() => setEditing(undefined)}
          onSave={async (value, reason) => update.mutateAsync({ setting: editing, value, reason })}
        />
      )}
    </>
  );
}

function SettingEditor({
  setting,
  busy,
  error,
  onClose,
  onSave,
}: {
  setting: EngineSetting;
  busy: boolean;
  error?: string;
  onClose(): void;
  onSave(value: string | boolean, reason: string): Promise<unknown>;
}) {
  const [value, setValue] = useState(setting.value);
  const [reason, setReason] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onSave(value, reason);
  }

  return (
    <div className="modal-layer">
      <button type="button" className="modal-scrim" onClick={onClose} aria-label="Close dialog" />
      <section className="modal" role="dialog" aria-modal="true" aria-label={`Change ${setting.key}`}>
        <header className="modal__header"><div><p className="eyebrow">Version {setting.version}</p><h2>Change engine setting</h2></div></header>
        <form className="stack-form" onSubmit={(event) => void submit(event)}>
          <label>
            {setting.key}
            {setting.type === "boolean" ? (
              <select value={String(value)} onChange={(event) => setValue(event.target.value === "true")}>
                <option value="false">Disabled</option><option value="true">Enabled</option>
              </select>
            ) : (
              <input required value={String(value)} onChange={(event) => setValue(event.target.value)} placeholder="0 */6 * * *" />
            )}
          </label>
          <p className="field-help">{setting.type === "cron" ? "Standard five-field cron expression. Invalid schedules are rejected." : "The worker or scheduler will adopt the value within roughly 15 seconds."}</p>
          <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this runtime policy changing?" /></label>
          {error && <div className="inline-error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="button button--primary" disabled={busy}>{busy ? "Saving…" : "Apply setting"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
